// warranty-toggle.js
// Self-contained warranty toggle for Shopify cart
;(function () {
  // Constants
  const TOGGLE_ID = "shipping-protection-toggle"
  const WARRANTY_PRODUCT_HANDLE = "brave-star-shipping-warranty" // Used to identify warranty items

  // Get the toggle if present
  function getToggle() {
    return document.getElementById(TOGGLE_ID)
  }

  // Render the toggle UI
  function renderToggle(hasWarranty, toggle) {
    if (!toggle) return
    toggle.innerHTML = hasWarranty
      ? `
      <span style="font-size:0.95em;color:#86c7d9;font-weight:600;">On</span>
      <span style="display:inline-block;width:32px;height:18px;background:#86c7d9;border-radius:9px;position:relative;vertical-align:middle;">
        <span style="display:block;width:16px;height:16px;background:#ffffff;border:1px solid #86c7d9;border-radius:50%;position:absolute;top:1px;left:15px;transition:left 0.2s;"></span>
      </span>
    `
      : `
      <span style="font-size:0.95em;color:#86c7d9;;font-weight:600;">Off</span>
      <span style="display:inline-block;width:32px;height:18px;background:#86c7d9;border-radius:9px;position:relative;vertical-align:middle;">
        <span style="display:block;width:16px;height:16px;background:#fff;border:1px solid #ccc;border-radius:50%;position:absolute;top:1px;left:1px;transition:left 0.2s;"></span>
      </span>
    `
    toggle.setAttribute("aria-pressed", hasWarranty ? "true" : "false")
    toggle.setAttribute("data-has-warranty", hasWarranty ? "true" : "false")
    // For accessibility, add a visible label if empty
    if (!toggle.innerText.trim()) {
      toggle.innerText = hasWarranty
        ? "Shipping Protection On"
        : "Shipping Protection Off"
    }
  }

  // Check cart for warranty
  async function checkWarrantyInCart() {
    try {
      const resp = await fetch("/cart.js")
      const cart = await resp.json()
      
      // Check if any item in the cart has the warranty product handle
      return cart.items.some(item => {
        return item.handle === WARRANTY_PRODUCT_HANDLE;
      });
    } catch (error) {
      console.error("[WarrantyToggle] Error checking warranty in cart:", error);
      return false;
    }
  }

  // Fully rerender the cart drawer section using Shopify's section rendering API
  // Rerender both the cart drawer and the full cart (if present)
  async function rerenderCartSections() {
    // Section IDs to try for full cart and drawer
    const sectionIds = [
      "cart-drawer",
      "cart-drawer-template",
      "cart",
      "cart-template"
    ]
    // Compose the query string for all sections
    const query = sectionIds.map((id) => `sections[]=${id}`).join("&")
    try {
      const response = await fetch(`/\?${query}`)
      if (!response.ok) throw new Error("Failed to fetch cart sections")
      const data = await response.json()
      // Update cart drawer
      // Helper to force-disable lazy loading for all images in a container
      function forceShowCartImages(container) {
        if (!container) return
        const imgs = container.querySelectorAll('img[data-rimg="lazy"]')
        imgs.forEach((img) => {
          if (img.dataset.rimgTemplate && img.dataset.rimgMax) {
            img.src = img.dataset.rimgTemplate.replace(
              "{size}",
              img.dataset.rimgMax
            )
            img.srcset = img.src
          }
          img.removeAttribute("data-rimg")
          // Remove rimg-canvas overlays if present
          const parent = img.parentElement
          if (parent) {
            const canvasDivs = parent.querySelectorAll("div[data-rimg-canvas]")
            canvasDivs.forEach((div) => div.remove())
          }
        })
      }

      for (const id of ["cart-drawer", "cart-drawer-template"]) {
        if (data[id]) {
          const drawer = document.querySelector("[data-cart-drawer]")
          if (drawer) {
            // ... existing template extraction logic ...
            // Check for <template data-html="content"> and extract its content if present
            const tempDiv = document.createElement("div")
            tempDiv.innerHTML = data[id]
            const tpl = tempDiv.querySelector('template[data-html="content"]')
            if (tpl) {
              drawer.innerHTML = tpl.innerHTML
              forceShowCartImages(drawer)
            } else {
              drawer.innerHTML = data[id]
              forceShowCartImages(drawer)
            }
          }
          // Re-initialize lazy images (for rimg.js)
          if (window.rimg && typeof window.rimg.update === "function") {
            window.rimg.update()
          }
        }
      }
      // Update full cart
      for (const id of ["cart", "cart-template"]) {
        if (data[id]) {
          // Try common cart container selectors
          const cartContainers = [
            document.querySelector("[data-cart]"),
            document.querySelector(".cart"),
            document.querySelector("#CartContainer"),
            document.querySelector(".cart-template")
          ].filter(Boolean)
          for (const container of cartContainers) {
            // Check for <template data-html="content"> and extract its content if present
            const tempDiv = document.createElement("div")
            tempDiv.innerHTML = data[id]
            const tpl = tempDiv.querySelector('template[data-html="content"]')
            if (tpl) {
              container.innerHTML = tpl.innerHTML
              forceShowCartImages(container)
            } else {
              container.innerHTML = data[id]
              forceShowCartImages(container)
            }
          }
          // Re-initialize lazy images (for rimg.js)
          if (window.rimg && typeof window.rimg.update === "function") {
            window.rimg.update()
          }
        }
      }
    } catch (err) {
      console.error("[WarrantyToggle] Cart sections rerender failed:", err)
    }
  }

  // Add warranty - calls getCartTotal() to get the correct variant ID at add time
  async function addWarranty() {
    try {
      // Get real-time warranty variant ID based on current cart total
      const variantId = await getCartTotal();
      
      if (!variantId) {
        console.error("[WarrantyToggle] Could not get valid warranty variant ID");
        return;
      }
      
      console.log("[WarrantyToggle] Adding warranty with variant ID:", variantId);
      
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });
      
      if (!res.ok) {
        const errText = await res.text();
        console.error("[WarrantyToggle] Failed to add warranty:", errText);
      }
      
      await rerenderCartSections();
    } catch (error) {
      console.error("[WarrantyToggle] Error adding warranty:", error);
    }
  }

  // Remove warranty
  async function removeWarranty() {
    try {
      const resp = await fetch("/cart.js")
      const cart = await resp.json()
      
      // Find all warranty items by product handle
      const warrantyItems = cart.items.filter(item => item.handle === WARRANTY_PRODUCT_HANDLE);
      
      if (warrantyItems.length > 0) {
        console.log("[WarrantyToggle] Removing warranty items:", warrantyItems.length);
        
        // Remove each warranty item
        for (const item of warrantyItems) {
          await fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: item.key, quantity: 0 })
          });
        }
        
        await rerenderCartSections();
      }
    } catch (error) {
      console.error("[WarrantyToggle] Error removing warranty:", error);
    }
  }

  // Main logic
  async function handleToggleClick(e) {
    e.preventDefault()
    const toggle = e.currentTarget
    toggle.disabled = true
    const hasWarranty = toggle.getAttribute("data-has-warranty") === "true"
    if (hasWarranty) {
      await removeWarranty()
    } else {
      await addWarranty()
    }
    // Wait for cart to update, then re-check
    setTimeout(async () => {
      const updated = await checkWarrantyInCart()
      renderToggle(updated, toggle)
      toggle.disabled = false
    }, 600) // Give Shopify time to update
  }

  let lastToggleInitialized = null
  async function initWarrantyToggle() {
    const toggle = getToggle()
    if (!toggle) {
      // Only log if we haven't already failed
      if (lastToggleInitialized !== null) {
        console.log("[WarrantyToggle] Button not found in DOM.")
        lastToggleInitialized = null
      }
      return
    }
    // Prevent infinite re-initialization on the same button
    if (toggle === lastToggleInitialized) return
    lastToggleInitialized = toggle
    toggle.style.background = "none"
    toggle.style.border = "none"
    toggle.style.padding = "0"
    toggle.style.margin = "0"
    toggle.style.cursor = "pointer"
    const hasWarranty = await checkWarrantyInCart()
    renderToggle(hasWarranty, toggle)
    toggle.removeEventListener("click", handleToggleClick) // Prevent double-binding
    toggle.addEventListener("click", handleToggleClick)
    console.log(
      "[WarrantyToggle] Initialized with state:",
      hasWarranty ? "ON" : "OFF"
    )
  }

  // Run on load
  document.addEventListener("DOMContentLoaded", initWarrantyToggle)

  // Observe cart drawer changes (AJAX)
  function getCartDrawerContainer() {
    return (
      document.querySelector(".cart-drawer__shipping-protection")?.parentElement
        ?.parentElement || document.body
    )
  }
  const observer = new MutationObserver(() => {
    const toggles = document.querySelectorAll("#shipping-protection-toggle")
    if (toggles.length > 1) {
      console.warn("[WarrantyToggle] Multiple toggles detected!")
      toggles.forEach((btn, i) => {
        if (i > 0) btn.remove()
      })
    }
    if (toggles.length === 1) {
      window.initWarrantyToggle()
    }
  })
  observer.observe(getCartDrawerContainer(), { childList: true, subtree: true })

  // Expose for manual re-init if needed
  window.initWarrantyToggle = initWarrantyToggle
})()
